import clsx from 'clsx';
import { Icon } from './icons';

/**
 * Numbered paging for lists small enough to have a knowable total.
 *
 * Deliberately not the "load more" the response feeds use. Those are cursor-paged
 * append-only collections with no total to show; this is for a set somebody is working
 * through, where "page 3 of 7" is orientation and being able to jump to the end is the
 * whole point.
 */
export interface PagerProps {
  page: number;
  totalPages: number;
  total: number;

  /** What the rows are, for the count line: "48 forms". */
  noun: string;
  onChange: (page: number) => void;

  /** Dims the controls mid-fetch without collapsing the layout. */
  busy?: boolean;
}

/**
 * The page numbers to draw, with nulls where a gap goes.
 *
 * First and last are always present, so the ends stay reachable in one click however long
 * the list is, and the current page keeps a neighbour on each side. Anything wider than
 * that is a row of numbers nobody reads.
 */
export function pageWindow(page: number, totalPages: number): (number | null)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const window = new Set([1, totalPages, page, page - 1, page + 1]);

  // Near an end the window is lopsided, so it is padded inwards - otherwise page 2 of 40
  // draws "1 2 3 … 40" while page 20 draws five numbers, and the control changes width as
  // you move through it.
  if (page <= 3) [2, 3, 4].forEach((n) => window.add(n));
  if (page >= totalPages - 2) [totalPages - 3, totalPages - 2, totalPages - 1].forEach((n) => window.add(n));

  const pages = [...window].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);

  const out: (number | null)[] = [];
  let previous = 0;

  for (const n of pages) {
    if (previous && n - previous > 1) out.push(null);
    out.push(n);
    previous = n;
  }

  return out;
}

export function Pager({ page, totalPages, total, noun, onChange, busy }: PagerProps) {
  // One page is not a choice, so there is nothing to offer. The count still is useful.
  const single = totalPages <= 1;

  return (
    <nav
      aria-label={`${noun} pages`}
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-[var(--ff-border)]
                 px-6 py-3"
    >
      <p className="text-[12.5px] text-[var(--ff-text-muted)]" aria-live="polite">
        {total.toLocaleString()} {total === 1 ? noun : `${noun}s`}
        {!single && <> · page {page} of {totalPages}</>}
      </p>

      {!single && (
        <div className={clsx('ml-auto flex items-center gap-1', busy && 'opacity-60')}>
          <Step
            label="Previous page"
            icon="chevronLeft"
            disabled={page <= 1}
            onClick={() => onChange(page - 1)}
          />

          {pageWindow(page, totalPages).map((n, i) => (n === null ? (
            // Not a button: there is no single page it would take you to.
            <span
              // eslint-disable-next-line react/no-array-index-key -- gaps have no identity
              key={`gap-${i}`}
              aria-hidden="true"
              className="px-1 text-[12.5px] text-[var(--ff-text-faint)]"
            >
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              aria-label={`Page ${n}`}
              aria-current={n === page ? 'page' : undefined}
              className={clsx(
                'h-8 min-w-8 rounded-md px-2 text-[12.5px] font-medium tabular-nums',
                'transition-colors duration-150',
                n === page
                  ? 'bg-[var(--ff-primary)] text-[var(--ff-on-primary)]'
                  : 'text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)]'
                    + ' hover:text-[var(--ff-text)]',
              )}
            >
              {n}
            </button>
          )))}

          <Step
            label="Next page"
            icon="chevronRight"
            disabled={page >= totalPages}
            onClick={() => onChange(page + 1)}
          />
        </div>
      )}
    </nav>
  );
}

function Step({ label, icon, disabled, onClick }: {
  label: string;
  icon: 'chevronLeft' | 'chevronRight';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md
                 text-[var(--ff-text-muted)] transition-colors duration-150
                 hover:bg-[var(--ff-surface-raised)] hover:text-[var(--ff-text)]
                 disabled:pointer-events-none disabled:opacity-40"
    >
      <Icon name={icon} />
    </button>
  );
}
